import { useMemo } from "react";
import {
  ChatBubbleBottomCenterTextIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";
import {
  PhoneArrowDownLeftIcon,
  PhoneArrowUpRightIcon,
  EnvelopeIcon,
  TagIcon,
  UserIcon,
  XMarkIcon,
  SpeakerXMarkIcon,
} from "@heroicons/react/24/solid";
import { CONVERSATION } from "../../models/Conversation";

import { FaWhatsapp, FaVoicemail } from "react-icons/fa";
import { RiWhatsappFill } from "react-icons/ri";
import { Badge } from "V2.0/common/badge";
import authService from "V2.0/services/authService";
import {
  FaFacebookMessenger,
  FaInstagram,
  FaNoteSticky,
} from "react-icons/fa6";
import { TbApi } from "react-icons/tb";
import { Tooltip } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import {
  MdTimerOff,
  MdOutlineSignalCellularOff,
  MdSupportAgent,
} from "react-icons/md";
import { PiPhoneTransferFill, PiPhonePauseFill } from "react-icons/pi";
import { LuServerOff } from "react-icons/lu";
import WhatsappMetaIcon from "./WhatsappMetaIcon";
import WhatsappMCubeEngageIcon from "./WhatsappMCubeEngageIcon";

// TODO: conversation refresh or remove new badge, if a human agent has visited the conversation
// TODO: on notification click, make the conversation id as the selected one

export const Conversation = {
  [CONVERSATION.WHATSAPP]: (props) => <WhatsappItem {...props} />,
  [CONVERSATION.WEB_CALL]: (props) => <WebCallItem {...props} />,
  [CONVERSATION.EMBEDDED_CHAT]: (props) => <EmbeddedChatItem {...props} />,
  [CONVERSATION.INBOUND_VOICE]: (props) => <InboundVoiceItem {...props} />,
  [CONVERSATION.OUTBOUND_VOICE]: (props) => <OutboundVoiceItem {...props} />,
  [CONVERSATION.SMS]: (props) => <SmsItem {...props} />,
  [CONVERSATION.MESSENGER]: (props) => <MessengerItem {...props} />,
  [CONVERSATION.API]: (props) => <ApiItem {...props} />,
  [CONVERSATION.INSTAGRAM]: (props) => <InstagramItem {...props} />,
  [CONVERSATION.MCUBE_ENGAGE_WHATSAPP]: (props) => <WhatsappItem {...props} />,
};

export const ConversationSourceIcon = {
  [CONVERSATION.WHATSAPP]: <WhatsappMetaIcon />,
  [CONVERSATION.WEB_CALL]: <GlobeAltIcon className="size-6 text-pink-500" />,
  [CONVERSATION.EMBEDDED_CHAT]: (
    <ChatBubbleBottomCenterTextIcon className="size-5 text-indigo-600" />
  ),
  [CONVERSATION.INBOUND_VOICE]: (
    <PhoneArrowDownLeftIcon className="size-5 text-green-500" />
  ),
  [CONVERSATION.OUTBOUND_VOICE]: (
    <PhoneArrowUpRightIcon className="size-5 text-amber-500" />
  ),
  [CONVERSATION.SMS]: <EnvelopeIcon className="size-5 text-blue-600" />,
  [CONVERSATION.MESSENGER]: (
    <FaFacebookMessenger className="size=5 text-blue-600" />
  ),
  [CONVERSATION.INSTAGRAM]: <FaInstagram className="size-5" />,
  [CONVERSATION.API]: <TbApi className="size-5" />,
  [CONVERSATION.MCUBE_ENGAGE_WHATSAPP]: <WhatsappMCubeEngageIcon />,
};

export function SourceIcon({ children }) {
  return (
    <div className="min-h-10 min-w-10 bg-white rounded-lg border flex justify-center items-center">
      {children}
    </div>
  );
}

function WebCallItem(props) {
  const { item } = props;

  return (
    <ConversationItem
      {...props}
      title={item.userIdentifier || item.sourceOfConversation.toLowerCase()}
      description={item.attributes?.callDuration}
    />
  );
}

function EmbeddedChatItem(props) {
  return <ConversationItem {...props} />;
}

function InboundVoiceItem(props) {
  const { item } = props;
  return (
    <ConversationItem
      {...props}
      title={item.userIdentifier}
      description={item.attributes?.callDuration}
    />
  );
}
function OutboundVoiceItem(props) {
  const { item } = props;
  return (
    <ConversationItem
      {...props}
      title={item.userIdentifier}
      description={item.attributes?.callDuration}
    />
  );
}
function SmsItem(props) {
  const { item } = props;
  const { unread, showAgentLabel } = useAssignedConversationItem(item);

  return (
    <ConversationItem
      {...props}
      title={item.userIdentifier}
      description={
        item?.messages[item.messages.length - 1]?.a ||
        item?.messages[item.messages.length - 1]?.q
      }
      unread={unread}
      showAgentLabel={showAgentLabel}
    />
  );
}

function useAssignedConversationItem(conversation) {
  const user = authService.getCurrentUser();

  const isUserTheCompanyOwner = user?.isCompanyOwner;
  const isAssigned =
    !!conversation?.humanAgentAssignmentSettings?.humanAgentAssigned;
  const isActive = conversation.status === "ACTIVE";
  const showAgentLabel = isAssigned && isActive && isUserTheCompanyOwner;

  const humanAgentAssignmentSettings =
    conversation?.humanAgentAssignmentSettings;
  let unread = false;

  if (
    humanAgentAssignmentSettings &&
    humanAgentAssignmentSettings?.humanAgentAssigned &&
    user.email === humanAgentAssignmentSettings?.humanAgentAssigned.email
  ) {
    const { messages } = conversation;
    const lastMessage = messages[messages.length - 1];

    if (
      conversation.status === "ACTIVE" &&
      (!lastMessage?.aTimestamp ||
        lastMessage?.aTimestamp <
          humanAgentAssignmentSettings?.humanAgentRequestedAt)
    ) {
      unread = true;
    }
  }

  return {
    unread,
    showAgentLabel,
  };
}

function MessengerItem(props) {
  const { item } = props;
  const { unread, showAgentLabel } = useAssignedConversationItem(item);

  return (
    <ConversationItem
      {...props}
      title={item.userIdentifier}
      description={
        item?.messages[item.messages.length - 1]?.a ||
        item?.messages[item.messages.length - 1]?.q
      }
      icon={<FaInstagram />}
      unread={unread}
      showAgentLabel={showAgentLabel}
    />
  );
}

function WhatsappItem(props) {
  const { item } = props;
  const user = authService.getCurrentUser();

  const isUserTheCompanyOwner = user?.isCompanyOwner;
  const isAssigned = !!item?.humanAgentAssignmentSettings?.humanAgentAssigned;
  const isActive = item.status === "ACTIVE";
  const showAgentLabel = isAssigned && isActive && isUserTheCompanyOwner;

  const humanAgentAssignmentSettings = item?.humanAgentAssignmentSettings;
  let unread = false;

  if (
    humanAgentAssignmentSettings &&
    humanAgentAssignmentSettings?.humanAgentAssigned &&
    user.email === humanAgentAssignmentSettings?.humanAgentAssigned.email
  ) {
    const { messages } = item;
    const lastMessage = messages[messages.length - 1];

    if (
      item.status === "ACTIVE" &&
      (!lastMessage?.aTimestamp ||
        lastMessage?.aTimestamp <
          humanAgentAssignmentSettings?.humanAgentRequestedAt)
    ) {
      unread = true;
    }
  }

  return (
    <ConversationItem
      {...props}
      title={item.userIdentifier}
      description={
        item?.messages[item.messages.length - 1]?.a ||
        item?.messages[item.messages.length - 1]?.q
      }
      icon={<FaWhatsapp />}
      unread={unread}
      showAgentLabel={showAgentLabel}
    />
  );
}

function ApiItem(props) {
  return <ConversationItem {...props} />;
}

function InstagramItem(props) {
  const { item } = props;
  const { unread, showAgentLabel } = useAssignedConversationItem(item);

  return (
    <ConversationItem
      {...props}
      // Prefer the resolved Instagram @handle (cached on the conversation attributes) over the raw
      // numeric sender id so the inbox shows a human-readable name; fall back to the id when unknown.
      title={
        item?.attributes?.instagramUsername
          ? `@${item.attributes.instagramUsername}`
          : item.userIdentifier
      }
      description={
        item?.messages[item.messages.length - 1]?.a ||
        item?.messages[item.messages.length - 1]?.q
      }
      icon={<FaInstagram />}
      unread={unread}
      showAgentLabel={showAgentLabel}
    />
  );
}

const renderTags = ({ item }) => {
  if (!item?.assignedTags || item?.assignedTags?.length === 0) {
    return null;
  }

  return (
    <Tooltip title="tag">
      <span className="flex items-center gap-x-1 text-xs font-semibold">
        <TagIcon className="size-4 text-blue-500" />
        {item.assignedTags.length}
      </span>
    </Tooltip>
  );
};

const renderNotes = ({ item }) => {
  if (!item?.notes || item?.notes?.length === 0) {
    return null;
  }

  return (
    <Tooltip title="notes">
      <span className="flex items-center gap-x-1 text-xs font-semibold">
        <FaNoteSticky className="size-4 text-yellow-500" />
        {item.notes.length}
      </span>
    </Tooltip>
  );
};

const CallEndReason = {
  UserHangup: "UserHangup",
  CallTransfer: "CallTransfer",
  AgentHangup: "AgentHangup",
  ServerError: "ServerError",
  UserInactivity: "UserInactivity",
  CallMaxTimeExceeded: "CallMaxTimeExceeded",
  Voicemail: "Voicemail",
  CallPutOnHold: "CallPutOnHold",
  TimedOut: "TimedOut",
  CallDropped: "CallDropped",
};

const CallEndReasonLabels = {
  [CallEndReason.UserHangup]: "User Hungup",
  [CallEndReason.CallTransfer]: "Call Transfered",
  [CallEndReason.AgentHangup]: "Agent Hungup",
  [CallEndReason.ServerError]: "Server Error",
  [CallEndReason.UserInactivity]: "User Inactive",
  [CallEndReason.CallMaxTimeExceeded]: "Call Time Exceeded",
  [CallEndReason.Voicemail]: "Voicemail",
  [CallEndReason.CallPutOnHold]: "Call on Hold",
  [CallEndReason.TimedOut]: "Timed Out",
  [CallEndReason.CallDropped]: "Call Dropped",
};

const CallEndReasonBadge = {
  [CallEndReason.UserHangup]: (
    <>
      <UserIcon className="size-4 text-blue-500" />
      <XMarkIcon className="size-3 text-red-500 " />
    </>
  ),
  [CallEndReason.CallTransfer]: (
    <PiPhoneTransferFill className="size-4 text-emerald-500" />
  ),
  [CallEndReason.AgentHangup]: (
    <>
      <MdSupportAgent className="size-4 text-blue-500" />
      <XMarkIcon className="size-3 text-red-500" />
    </>
  ),
  [CallEndReason.ServerError]: <LuServerOff className="size-4 text-red-500" />,
  [CallEndReason.UserInactivity]: (
    <>
      <UserIcon className="size-4 text-blue-500" />
      <SpeakerXMarkIcon className="size-3 text-red-500" />
    </>
  ),
  [CallEndReason.CallMaxTimeExceeded]: (
    <MdTimerOff className="size-4 text-amber-500" />
  ),
  [CallEndReason.Voicemail]: <FaVoicemail className="size-4 text-purple-500" />,
  [CallEndReason.CallPutOnHold]: (
    <PiPhonePauseFill className="size-4 text-amber-500" />
  ),
  [CallEndReason.TimedOut]: <MdTimerOff className="size-4 text-purple-500" />,
  [CallEndReason.CallDropped]: (
    <MdOutlineSignalCellularOff className="size-4 text-amber-500" />
  ),
};

const renderCallEndReason = ({ item }) => {
  if (!item?.extractedData?.callEndReason) {
    return null;
  }

  const callEndReasonIcon = CallEndReasonBadge[
    item?.extractedData?.callEndReason
  ] ?? <Badge>{item?.extractedData?.callEndReason}</Badge>;

  const callEndReasonLabel =
    CallEndReasonLabels[item?.extractedData?.callEndReason] ??
    item?.extractedData?.callEndReason;

  return (
    <Tooltip title={callEndReasonLabel}>
      <span className="flex items-start text-xs font-semibold">
        {callEndReasonIcon}
      </span>
    </Tooltip>
  );
};

export function ConversationItem(props) {
  const {
    item,
    isSelected,
    title,
    description,
    updateSelectedConversation,
    unread,
    showAgentLabel,
    assistantName,
    setShowDetails,
    setScrollToView,
  } = props;

  const navigate = useNavigate();
  const location = useLocation();

   const renderInfoIcons = useMemo(() => {
    if (!item) {
      return () => null;
    }

    const shouldRender =
      (item?.assignedTags && item?.assignedTags?.length > 0) ||
      (item?.notes && item?.notes?.length > 0) ||
      (item?.extractedData &&
        Object.keys(item?.extractedData).length > 0 &&
        item?.extractedData?.callEndReason);

    if (!shouldRender) {
      return () => null;
    }

    return ({ renderProps }) => (
      <div className="flex justify-between">
        <div className="flex items-center gap-x-3">
          {renderProps.map((renderProp) => renderProp({ item }))}
        </div>
      </div>
    );
  }, [item]);

  return (
    <div
      key={item._id}
      id={item._id}
      onClick={() => {
        updateSelectedConversation(item);
        setScrollToView && setScrollToView(false);
        setShowDetails && setShowDetails(true);
        navigate({pathname: `/threads/${item._id}`, search: location.search});
      }}
      className={`flex items-start p-4 overflow-hidden  transition duration-150 ease-in-out cursor-pointer ${
        isSelected ? "bg-gray-100" : "hover:bg-gray-50"
      }`}
    >
      <SourceIcon>
        {ConversationSourceIcon[item.sourceOfConversation]}
      </SourceIcon>
      <div className="ml-4 flex flex-col w-full min-w-0 gap-y-1">
        <div className="flex justify-between items-center w-full min-w-0 gap-x-2">
          {/* min-w-0 + truncate so a long numeric userIdentifier ellipsizes instead of pushing the
              right-aligned badges (Filtered / new / agent) off the edge of the list panel. */}
          <p className="text-base font-normal text-gray-700 truncate min-w-0">
            {title || item.sourceOfConversation}{" "}
          </p>
          {(unread || showAgentLabel || item.status === "FILTERED") && (
            <div className="flex gap-x-2 shrink-0">
              {item.status === "FILTERED" && (
                <div>
                  <Badge color="zinc">Filtered</Badge>
                </div>
              )}
              {unread && (
                <div>
                  <Badge color="green">new</Badge>
                </div>
              )}
              {showAgentLabel && (
                <div>
                  <Badge color="blue">
                    {item?.humanAgentAssignmentSettings?.humanAgentAssigned?.name?.split(
                      " "
                    )[0] || "Agent"}
                  </Badge>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-between items-center min-w-0 gap-x-2">
          <p className="shrink text-sm text-gray-400 truncate min-w-0">
            {description || ""}
          </p>
          {assistantName && !showAgentLabel && (
            <div className="shrink-0">
              <Badge>
                <Tooltip title={assistantName}>
                  <p className="truncate max-w-20">{assistantName}</p>
                </Tooltip>
              </Badge>
            </div>
          )}
        </div>

         {renderInfoIcons({
          renderProps: [renderNotes, renderTags, renderCallEndReason],
        })}
      </div>
    </div>
  );
}

export function ConversationItemLoading(props) {
  return (
    <div className="flex animate-pulse items-center p-4 overflow-hidden space-x-4 transition duration-150 ease-in-out cursor-pointer w-full">
      <div className="min-h-10 min-w-10 bg-zinc-200 rounded-lg flex justify-center items-center"></div>
      <div className="w-full flex flex-col gap-y-2">
        <div className="bg-zinc-200 h-4 w-40 rounded-sm"></div>
        <div className="flex items-center justify-between">
          <div className="bg-zinc-200 h-4 w-16 rounded-sm"></div>
          <div className="bg-zinc-200 h-4 w-16 rounded-sm mt-2  "></div>
        </div>
      </div>
    </div>
  );
}
